import { useEffect } from 'react';
import { useCanvas } from '../state/canvas-store.js';
import type { AppState } from '@extapia/shared';

const TOOL_SHORTCUTS: Record<string, AppState['currentTool']> = {
  v: 'selection',
  h: 'hand',
  r: 'rect',
  d: 'diamond',
  o: 'ellipse',
  a: 'arrow',
  l: 'line',
  p: 'freehand',
  t: 'text',
  s: 'sticky',
  f: 'frame',
  e: 'eraser',
};

export const useKeyboardShortcuts = (): void => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore when typing in input/textarea/contenteditable
      const t = e.target as HTMLElement | null;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable) return;

      const key = e.key.toLowerCase();
      const meta = e.ctrlKey || e.metaKey;
      const st = useCanvas.getState();

      // History
      if (meta && key === 'z' && !e.shiftKey) { e.preventDefault(); st.undo(); return; }
      if (meta && (key === 'y' || (key === 'z' && e.shiftKey))) { e.preventDefault(); st.redo(); return; }

      // Clipboard
      if (meta && key === 'a') { e.preventDefault(); st.selectAll(); return; }
      if (meta && key === 'c') { e.preventDefault(); st.copy(); return; }
      if (meta && key === 'x') { e.preventDefault(); st.cut(); return; }
      if (meta && key === 'v') { e.preventDefault(); st.paste(); return; }
      if (meta && key === 'd') { e.preventDefault(); st.duplicateSelected(); return; }

      // Layer ops
      if (meta && e.shiftKey && key === ']') { e.preventDefault(); st.bringToFront(); st.commit(); return; }
      if (meta && e.shiftKey && key === '[') { e.preventDefault(); st.sendToBack(); st.commit(); return; }
      if (meta && !e.shiftKey && key === ']') { e.preventDefault(); st.bringForward(); st.commit(); return; }
      if (meta && !e.shiftKey && key === '[') { e.preventDefault(); st.sendBackward(); st.commit(); return; }

      // Zoom reset
      if (meta && key === '0') { e.preventDefault(); st.setViewport({ zoom: 1, scrollX: 0, scrollY: 0 }); return; }

      // Delete
      if (key === 'delete' || key === 'backspace') {
        if (st.selectedIds.size > 0) { e.preventDefault(); st.deleteSelected(); st.commit(); }
        return;
      }
      // Escape
      if (key === 'escape') {
        st.clearSelection();
        st.setTool('selection');
        st.stopTextEdit();
        return;
      }

      // Tool shortcuts (single-key, no modifier)
      const tool = TOOL_SHORTCUTS[key];
      if (tool && !meta && !e.altKey) st.setTool(tool);

      // Help
      if (key === '?' || (e.shiftKey && key === '/')) {
        window.dispatchEvent(new CustomEvent('open-help'));
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
};
